import Element from "./_element";

// Extracted from _setup.js so that js/elements.js can register <blue-composition> and
// <blue-component> from the document head, before the first paint.
//
// Element._create() moves every child into a slotted wrapper and attaches a shadow root. While
// the definitions only arrived with the deferred index.js bundle, that re-parenting landed after
// the page had painted and Chrome scored it as unexpected layout shift (PageSpeed "Layout shift
// culprits": div[slot=content] + blue-composition = 0.295 of a 0.311 mobile CLS). Upgrading during
// parsing produces the identical final DOM with no layout-shift entries.
//
// Two constraints follow from running in <head> instead of at the end of <body>:
//
//  1. ResizeObserver may not exist yet — polyfills.js supplies it for iOS 13 and is deferred, so
//     it runs later. Observation is therefore held until DOMContentLoaded, by which point both
//     deferred bundles have executed. Only the re-parenting has to happen early; the size /
//     on-screen / visible-ratio attributes are not paint-critical.
//  2. This code, plus _element.js and utils/_createElement.js, is bundled into a script that
//     blocks rendering, so it avoids constructs babel rewrites into core-js imports (Set/Map,
//     spread of iterables, Array#forEach/#some/#sort, parseInt, private class members). Those
//     helpers were 32 KB of the 33 KB bundle. Plain loops keep js/elements.js around 2 KB.

let breakpoints;
let resize;
let intersection;
let observing = false;

function getBreakpoints() {
  if (breakpoints) return breakpoints;

  const doc = getComputedStyle(document.documentElement);

  // Hand-ordered descending, which is what the old breakpoints.sort() produced:
  // --xl > --lg > --md > --sm > --xs in scss/abstracts/_variables.scss.
  // parseInt is the one construct left that babel still swaps for core-js (~2 KB of the bundle);
  // reading through `self` does not avoid it and hand-parsing the px values is not worth it.
  breakpoints = [
    ["XL", parseInt(doc.getPropertyValue("--xl"))],
    ["LG", parseInt(doc.getPropertyValue("--lg"))],
    ["MD", parseInt(doc.getPropertyValue("--md"))],
    ["SM", parseInt(doc.getPropertyValue("--sm"))],
    ["XS", parseInt(doc.getPropertyValue("--xs"))]
  ];

  return breakpoints;
}

function createObservers() {
  if (resize || typeof ResizeObserver === "undefined") return;

  resize = new ResizeObserver(entries => {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const comp = entry.target.__comp;
      if (!comp) continue; // guard undefined before reading breakpoints

      const width = entry.contentRect.width;
      const current = entry.target.dataset.size;
      const bps = getBreakpoints();

      for (let b = 0; b < bps.length; b++) {
        if (width >= bps[b][1]) {
          if (current !== bps[b][0]) {
            comp.setAttribute("size", bps[b][0]);
          }
          break;
        }
      }
    }
  });

  intersection = new IntersectionObserver(
    entries => {
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const comp = entry.target.__comp;
        if (!comp) continue; // same guard for intersection callback

        comp.setAttribute("on-screen", entry.isIntersecting);
        comp.setAttribute(
          "visible-ratio",
          (Math.round(entry.intersectionRatio * 4) / 4).toFixed(2)
        );
      }
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1]
    }
  );
}

function observe(element) {
  createObservers();
  if (!resize) return;

  resize.observe(element);
  intersection.observe(element);
}

// polyfills.js and index.js are both deferred, so both have executed by DOMContentLoaded.
// Elements that upgraded before then are picked up in one sweep: Element._create() keeps the
// slotted wrapper in the light DOM, so nested components are still reachable from the document.
function startObserving() {
  observing = true;

  const elements = document.querySelectorAll("blue-component[observe]");
  for (let i = 0; i < elements.length; i++) {
    observe(elements[i]);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startObserving, { once: true });
} else {
  startObserving();
}

export default class Component extends Element {
  constructor() {
    super();
  }

  // A component carries its own size / on-screen / visible-ratio attributes; the base class
  // points __comp at the first slotted child instead. Set through _link() rather than in
  // connectedCallback because the base class re-links on every absorb while the page parses.
  _link() {
    this.__comp = this;
  }

  connectedCallback() {
    super.connectedCallback(); // initialize shadow DOM from Element base class

    // Before DOMContentLoaded, startObserving() picks this up instead.
    if (observing && this.hasAttribute("observe")) {
      observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!resize) return;
    resize.unobserve(this);
    intersection.unobserve(this);
  }
}
